import { Input } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessToast, showUserDbErrorToast } from 'utils/toasts';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { getFileExtension, getFilenameWithoutExtension } from '../utils/fileUtils';

const BpUpdateFileEntryMutationDocument = graphql(`
  mutation BpUpdateFileEntry($where: FileEntryWhere!, $update: FileEntryUpdateInput!) {
    updateFileEntries(where: $where, update: $update) {
      fileEntries {
        __typename
        uuid
      }
    }
  }
`);

type FileRenameFormType = {
  name: string;
};

type FileRenameFormProps = {
  fileUuid: string;
  name: string;
  onRename: (name: string) => void;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const FileRenameForm = ({ fileUuid, name, onRename, onLoad, onClose }: FileRenameFormProps) => {
  const { t } = useTranslation();

  const [, updateFileEntry] = useMutation(BpUpdateFileEntryMutationDocument);

  const initalValues: FileRenameFormType = {
    name: getFilenameWithoutExtension(name),
  };

  async function validate(values: FileRenameFormType) {
    if (!values.name) {
      return t('files.validation.nameTooShort');
    }
  }

  async function handleSubmit(values: FileRenameFormType, helpers: FormikHelpers<FileRenameFormType>) {
    onLoad(true);

    const newName = `${values.name}.${getFileExtension(name)}`;

    const { error } = await updateFileEntry({
      where: {
        uuid: fileUuid,
      },
      update: {
        filename: newName,
      },
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
    onRename(newName);
  }

  return (
    <Formik initialValues={initalValues} onSubmit={handleSubmit} validate={validate}>
      {({ values, handleBlur, handleChange, isSubmitting, errors }) => (
        <Form>
          <Input
            name={'name'}
            onBlur={handleBlur}
            onChange={handleChange}
            value={values.name?.toString()}
            error={errors.name?.toString()}
            autoFocus
          />
          <ModalBottomButtons closeButton={{ callback: onClose }} isLoading={isSubmitting} errors={errors} />
        </Form>
      )}
    </Formik>
  );
};
